<template>
  <v-row class="align-center py-0">
    <v-col class="d-flex align-center" cols="1">
      <div class="d-flex flex-column mr-1">
        <v-icon size="small" @click="$emit('rowUp', rowNum)">mdi-menu-up</v-icon>
        <v-icon size="small" @click="$emit('rowDown', rowNum)">mdi-menu-down</v-icon>
      </div>
      <MegaSelect
        :row="rowNum"
        :col="0"
        :options="frontTypeOptions"
        :displayValue="true"
        v-model:data="data.frontType"
        placeholder="Item type"
        @change="typeChange"
      ></MegaSelect>
    </v-col>
    <v-col class="d-flex justify-space-between">
      <MegaCell v-model:data="data.itemNum" :row="rowNum" :col="1" dataType="String"></MegaCell>
      <v-tooltip v-if="tip" location="top" :open-delay="500">
        <template v-slot:activator="{ props }">
          <v-icon size="small" class="mr-1" v-bind="props">mdi-information</v-icon>
        </template>
        <span>{{ tip }}</span>
      </v-tooltip>
    </v-col>
    <v-col cols="1" class="d-flex">
      <v-icon
        :disabled="
          ['drw-int', 'lhd-b-2part', 'rhd-b-2part', 'lhd-b-cnr', 'rhd-b-cnr', 'lhd-w-cnr', 'rhd-w-cnr'].includes(
            data.frontType
          )
        "
        :color="hasJoiningSet ? 'info' : undefined"
        @click="joiningDialog = true"
        >mdi-bandage</v-icon
      >
    </v-col>
    <v-col>
      <MegaSelect
        v-if="usesPresetDropdown"
        :row="rowNum"
        :col="2"
        :options="wOptions || []"
        v-model:data="data.w"
        placeholder="Select width"
        noOptions="No presets available"
      ></MegaSelect>
      <MegaCell
        v-else
        :presets="wPresets && typeof wPresets[0] === 'number' ? wPresets[0] : null"
        v-model:data="data.w"
        :unit="region == 'us' ? 'inch' : 'mm'"
        dataType="Number"
        :row="rowNum"
        :col="2"
        @change="filterHandleOptions"
      ></MegaCell>
    </v-col>
    <v-col>
      <MegaSelect
        v-if="usesPresetDropdown"
        :row="rowNum"
        :col="3"
        :options="hOptions || []"
        v-model:data="data.h"
        placeholder="Select height"
        noOptions="No presets available"
      ></MegaSelect>
      <MegaCell
        v-else
        :presets="hPresets && typeof hPresets[0] === 'number' ? hPresets[0] : null"
        v-model:data="data.h"
        :unit="region == 'us' ? 'inch' : 'mm'"
        dataType="Number"
        :row="rowNum"
        :col="3"
        :warn="heightWarn"
        @change="filterHandleOptions"
      ></MegaCell>
    </v-col>
    <v-col>
      <MegaCell
        :presets="qtyPresets ? qtyPresets[0] : ['top', 'mid', 'bot'].includes(data.joiningPos) ? 1 : null"
        :disabled="
          ['top', 'mid', 'bot'].includes(data.joiningPos) ||
          ['lhd-b-cnr', 'rhd-b-cnr', 'lhd-w-cnr', 'rhd-w-cnr'].includes(data.frontType)
        "
        v-model:data="data.qty"
        dataType="Number"
        :row="rowNum"
        :col="4"
      ></MegaCell>
    </v-col>
    <v-col cols="2" class="d-flex align-center">
      <div
        v-if="data.material"
        class="colour mr-2"
        :style="{ 'background-color': findMaterialHex(data.material) }"
      ></div>
      <MegaSelect
        :row="rowNum"
        :col="5"
        :options="filteredMaterials"
        v-model:data="data.material"
        placeholder="Select material"
        noOptions="No materials created yet"
      ></MegaSelect>
    </v-col>
    <v-col cols="2">
      <MegaSelect
        :row="rowNum"
        :col="6"
        :disabled="!data.frontType"
        :options="filteredHandleOptions"
        v-model:data="data.handle"
        placeholder="Set Width then Select handle"
        noOptions="No handles created yet"
      ></MegaSelect>
    </v-col>
    <v-col class="d-flex" :order="wonky ? 'first' : 'last'" :class="[{ 'justify-end': !wonky }]" cols="2">
      <v-tooltip location="top" :open-delay="500">
        <template v-slot:activator="{ props }">
          <v-icon size="small" class="mr-1" @click="$emit('copy', rowNum)" v-bind="props">mdi-content-copy</v-icon>
        </template>
        <span>Copy</span>
      </v-tooltip>
      <v-tooltip location="top" :open-delay="500">
        <template v-slot:activator="{ props }">
          <v-icon size="small" class="mr-1" @click="$emit('copyBottom', rowNum)" v-bind="props"
            >mdi-arrow-collapse-down</v-icon
          >
        </template>
        <span>Copy to bottom</span>
      </v-tooltip>
      <v-tooltip location="top" :open-delay="500">
        <template v-slot:activator="{ props }">
          <v-icon size="small" @click="$emit('delete', rowNum)" v-bind="props">mdi-delete</v-icon>
        </template>
        <span>Delete</span>
      </v-tooltip>
    </v-col>

    <!-- Joining Dialog -->
    <v-dialog v-model="joiningDialog" width="600">
      <v-card>
        <v-card-title class="bg-primary text-white text-h6">Joining Option</v-card-title>
        <v-card-text class="pt-4">
          <p>Select position of the door when joining.</p>
          <v-select :items="positionOptions" v-model="data.joiningPos" label="Position" />
          <p v-if="!disableJoiningDouble" class="text-red">
            Check box for inserts that are double handle looking and joined.
            <br />
            Only for "double-handle j-bracket" OR "SRG/Arc facing each other"
            <br />
            (wall-PAXSPLIT/PLATSA joined NEEDS double)
          </p>
          <v-checkbox
            v-model="data.joiningDouble"
            label="Joined Double Handles"
            v-if="!disableJoiningDouble"
          ></v-checkbox>
          <p>*Item engraving will include joining info automatically; just input item number.</p>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import productData from '@/js/productData'

import MegaSelect from '@/components/quoting/items/MegaSelect'
import MegaCell from '@/components/quoting/items/MegaCell'

export default {
  name: 'Front',
  props: ['rowNum', 'data', 'materials', 'handles', 'wonky', 'region'],
  components: { MegaCell, MegaSelect },
  data() {
    return {
      frontTypes: productData.frontTypes,
      frontTypeOptions: [],
      joiningDialog: false,
      handleOptions: this.handles,
      positionOptions: [
        {
          text: 'Top - brackets added to bottom of door',
          value: 'top'
        },
        {
          text: 'Middle - brackets added to top and bottom of door',
          value: 'mid'
        },
        {
          text: 'Bottom - brackets added to top of door',
          value: 'bot'
        },
        {
          text: 'Cancel Joining',
          value: 'cancel'
        }
      ]
    }
  },
  created() {
    this.filterFrontTypeOptions()

    if (this.data?.itemNum) {
      this.markJoinedDoor(this.data.joiningPos)
      this.markDoubleJoinedDoor(this.data.joiningDouble)
    }
  },
  watch: {
    'data.handle': function () {
      this.filterFrontTypeOptions()
    },
    'data.joiningPos': function (val) {
      this.markJoinedDoor(val)
    },
    'data.joiningDouble': function (val) {
      this.markDoubleJoinedDoor(val)
    }
  },

  computed: {
    tip() {
      let tip = null
      if (this.data.frontType && productData.getFrontType(this.data.frontType).tip) {
        tip = productData.getFrontType(this.data.frontType).tip
      }
      return tip
    },
    filteredMaterials() {
      return this.materials.filter((material) => {
        return material.thickness == 18
      })
    },
    // The list the dropdown actually gets. `handleOptionsForFront` below narrows by front type and
    // dimensions, and a handle saved on an existing row does not always survive that — change a
    // width and the handle already set can drop out of its own options.
    //
    // When that happens V3 renders the id instead of the name. `transformItem` resolves a selection's
    // title as `getPropertyFromItem(item, itemTitle, item)`, where the third argument is the
    // FALLBACK: no matching item means the accessor is handed the bare id string, `o.label` comes
    // back undefined, and Vuetify falls back to the item itself — the id. Keeping the current handle
    // in the list gives the accessor a real object to read a name from.
    filteredHandleOptions() {
      const options = this.handleOptionsForFront ?? []
      if (!this.data.handle || options.some((o) => o.value === this.data.handle)) return options
      const current = this.handles.find((o) => o.value === this.data.handle)
      return current ? [...options, current] : options
    },
    handleOptionsForFront() {
      let frontTypeArray
      let specialType
      if (this.data && this.data.frontType) {
        frontTypeArray = this.data.frontType.includes('-') ? this.data.frontType.split('-') : [this.data.frontType]
        specialType = frontTypeArray.length == 3 ? frontTypeArray[2] : null
      }

      if (this.data.frontType && specialType && specialType === '2part') {
        return this.handles.filter((handle) => {
          if (handle.type === 'lrgSemiCir') {
            return this.data.h >= 100 && this.data.w >= 100
          }
          return !(
            (handle.type === 'j' && handle.orientation === 'v') ||
            (handle.type.split('-')[0] == 'dpull' && handle.type.split('-').includes('l')) ||
            ['lrgSemiGrab', 'midSemiGrab'].includes(handle.type) ||
            handle.orientation === 'updn'
          )
        })
      } else if (this.data.frontType && this.data.frontType === 'drw') {
        return this.handles.filter((handle) => {
          if (['v', 'vc', 'vbt', 'vwb'].includes(handle.orientation)) return false
          if (handle.type === 'lrgSemiCir' && (!this.data.h || this.data.h < 100)) return false
          const srgWidth = productData.srgHandleWidths[handle.type]
          if (srgWidth && this.data.w && this.data.w < srgWidth + 60) return false
          return true
        })
      } else if (this.data.frontType && ['thd', 'bhd'].includes(this.data.frontType.split('-')[0])) {
        return this.handles.filter((handle) => {
          if (handle.type === 'lrgSemiCir' && (!this.data.h || this.data.h < 100)) return false
          return !['v', 'vc', 'vbt', 'vwb'].includes(handle.orientation)
        })
      } else {
        return productData.getHandleOptions(this.data.frontType, this.handles, this.data.w, this.data.h, this.region)
      }
    },
    disableJoiningDouble() {
      if (this.data.handle && ['top', 'mid', 'bot'].includes(this.data.joiningPos)) {
        let selectedHandleInfo = this.getHandleType(this.data?.handle)
        if (
          (selectedHandleInfo.type === 'j' && selectedHandleInfo.orientation === 'h') ||
          (['semiGrab', 'midSemiGrab', 'lrgSemiGrab'].includes(selectedHandleInfo.type) &&
            ['h', 'c', 'updn'].includes(selectedHandleInfo.orientation)) ||
          ['halfArc', 'lrgHalfArc', 'quartArc', 'lrgQuartArc'].includes(selectedHandleInfo.type)
        ) {
          return false
        } else {
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.data.joiningDouble = false
          return true
        }
      } else {
        return true
      }
    },
    usesPresetDropdown() {
      return [
        'drw-int',
        'lhd-w-pantry',
        'rhd-w-pantry',
        'lhd-b-pax',
        'rhd-b-pax',
        'lhd-b-paxsplit',
        'rhd-b-paxsplit',
        'lhd-w-paxsplit',
        'rhd-w-paxsplit'
      ].includes(this.data.frontType)
    },
    rootPresets() {
      let presets = productData.getFrontType(this.data.frontType)?.presets
      if (!presets) return null

      return presets?.[this.region] || null
    },
    wPresets() {
      if (this.rootPresets) {
        return this.rootPresets.w
      } else {
        return null
      }
    },
    hPresets() {
      if (this.rootPresets) {
        return this.rootPresets.h
      } else {
        return null
      }
    },
    wOptions() {
      return this.withCurrentValue(this.wPresets, this.data.w)
    },
    hOptions() {
      return this.withCurrentValue(this.hPresets, this.data.h)
    },
    qtyPresets() {
      if (this.rootPresets) {
        return this.rootPresets.qty
      } else {
        return null
      }
    },
    heightWarn() {
      if (!this.data.h || !this.data.material) return false
      const material = this.materials.find((m) => m.value === this.data.material)
      if (!material) return false
      const threshold = this.region === 'us' ? 1270 : 1400
      return this.data.h >= threshold && material.hasGrain === true
    },
    hasJoiningSet() {
      return this.data.joiningPos && ['top', 'mid', 'bot'].includes(this.data.joiningPos)
    }
  },
  methods: {
    withCurrentValue(presets, value) {
      if (!Array.isArray(presets)) return []
      const num = Number(value)
      if (!Number.isFinite(num) || num <= 0) return presets
      if (presets.some((preset) => preset.value === num)) return presets
      const display = this.region === 'us' ? this.$utils.toFixedNumber(num / 25.4, 3) : num
      return [...presets, { label: `${display} (custom)`, value: num }]
    },
    typeChange() {
      // set handle options
      this.handleOptions = this.filterHandleOptions()
    },
    filterHandleOptions() {
      let selection = productData.getHandleOptions(
        this.data.frontType,
        this.handles,
        this.data.w,
        this.data.h,
        this.region
      )
      selection.forEach((handle) => {
        if (!Object.values(handle)[0] === this.data.handle) {
          this.data.handle = ''
        }
      })
      return selection
    },
    getHandleType(id) {
      return this.handles.find((handle) => {
        return handle.value === id
      })
    },
    markJoinedDoor(joined) {
      // reset joining position if joining is cancelled.
      if (joined === 'cancel') {
        this.data.joiningPos = null
        this.data.joiningDouble = false
        return
      }

      // Legacy check: if already marked with J.
      if (this.data.itemNum.toString().split('').includes('J')) {
        // remove all non-numerical characters except for any 'D' or 'd' characters.
        this.data.itemNum = this.data.itemNum.toString().replace(/[^0-9Dd]/g, '')
      }

      if (joined) {
        if (!this.data.itemNum.toString().split('').includes('J')) {
          this.data.itemNum += 'J'
          this.data.itemNum += `-${this.data.joiningPos.split('')[0].toUpperCase()}`
        }
      } else {
        if (this.data.itemNum.toString().split('').includes('J')) {
          this.data.itemNum = this.data.itemNum.toString().replace(/\D/g, '')
        }
      }
    },
    markDoubleJoinedDoor(double) {
      // Legacy check: if already marked with D.
      if (this.data.itemNum.toString().split('').includes('D')) {
        // remove the D
        this.data.itemNum = this.data.itemNum.toString().replace('D', '')
      }

      if (double) {
        if (!this.data.itemNum.toString().split('').includes('D')) {
          this.data.itemNum += 'D'
        }
      } else {
        if (this.data.itemNum.toString().split('').includes('D')) {
          this.data.itemNum = this.data.itemNum.toString().replace('D', '')
        }
      }
    },
    filterFrontTypeOptions() {
      let selectedHandleInfo = this.getHandleType(this.data.handle)
      if (selectedHandleInfo?.type == 'quarterCircleGrab' || selectedHandleInfo?.type == 'lrgQuarterCircleGrab') {
        this.frontTypeOptions = this.frontTypes.filter((type) => {
          return ['lhd', 'rhd'].includes(type.value.split('-')[0])
        })
      } else {
        this.frontTypeOptions = this.frontTypes
      }
    },
    findMaterialHex(materialCode) {
      if (!materialCode) return []
      const match = this.materials.find((material) => {
        return material.value === materialCode
      })
      return match.hex
    }
  }
}
</script>

<style lang="scss" scoped>
.row {
  border-bottom: solid 1px #eee;
  .col {
    padding-top: 0px;
    padding-bottom: 0px;
  }
}
.colour {
  min-width: 25px;
  width: 25px;
  height: 25px;
  border: solid 1px #999;
  border-radius: 3px;
  margin-right: 12px;
}
</style>
